import { useCallback, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { Toast } from "@sscs/ui";
import { bytesToGB } from "@sscs/utils";
import { useCurrentOrganization, usePercentageSlider } from "Hooks";
import { Group } from "Types";
import { MAX_PERCENTAGE, apiErrorToast, clampPercentageInput } from "Utils";
import { useUpdateGroupMutation } from "../../services/organizations.api";
import {
  GroupFormData,
  GroupFormPercentageSlider,
  GroupFormReturn,
} from "../../types";
import { generateGroupFormSchema, getEditGroupFormBody } from "../../utils";

type UseEditGroupFormProps = {
  filestreamPercentageFieldName: keyof GroupFormPercentageSlider;
  projectsPercentageFieldName: keyof GroupFormPercentageSlider;
  group: Group;
  onClose: () => void;
  totalPortalCapacity?: number;
};

const useEditGroupForm = ({
  filestreamPercentageFieldName,
  projectsPercentageFieldName,
  group,
  onClose,
  totalPortalCapacity,
}: UseEditGroupFormProps): GroupFormReturn => {
  const currentOrganization = useCurrentOrganization();
  const [updateGroup] = useUpdateGroupMutation();

  const groupLimit = group.limit;

  const {
    schema: percentageSliderSchema,
    formDefaultValues,
    getSliderPercentages,
    onChangePercentage,
  } = usePercentageSlider<GroupFormPercentageSlider>({
    firstPercentageName: filestreamPercentageFieldName,
    secondPercentageName: projectsPercentageFieldName,
    defaultSliderValue: groupLimit?.filestream_percentage ?? 50,
  });

  // we can define our form schema in a way that asserts it type against type GroupData
  const editGroupSchema = useMemo(
    () => generateGroupFormSchema(percentageSliderSchema, totalPortalCapacity),
    [percentageSliderSchema, totalPortalCapacity],
  );

  const defaultValues = useMemo(
    () => ({
      ...formDefaultValues,
      limited: groupLimit?.limit_bytes ? bytesToGB(groupLimit.limit_bytes) : 0,
      name: group.name ?? "",
      description: group.description ?? "",
      is_default: group.is_default ?? "",
      oidc_sso_group_mappings: group.oidc_sso_group_mappings ?? [],
    }),
    [
      formDefaultValues,
      groupLimit?.limit_bytes,
      group.description,
      group.name,
      group.is_default,
      group.oidc_sso_group_mappings,
    ],
  );

  const formMethods = useForm<GroupFormData>({
    resolver: yupResolver(editGroupSchema),
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  const onSubmit: SubmitHandler<GroupFormData> = async (data) => {
    if (!isDirty) {
      onClose();
      return;
    }

    try {
      await updateGroup({
        groupId: group.group_id,
        body: getEditGroupFormBody(data, currentOrganization?.id ?? ""),
      }).unwrap();

      Toast.show({
        title: "Group edited",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not edit group");
    }

    onClose();
  };

  const filestream_percentage = watch(filestreamPercentageFieldName);
  const {
    firstPercentage: filestreamPercentage,
    secondPercentage: projectsPercentage,
  } = getSliderPercentages(filestream_percentage);

  const onPercentageSliderChange = useCallback(
    (value: number) => {
      onChangePercentage(value, setValue);
    },
    [onChangePercentage, setValue],
  );

  const onProjectsPercentageInputChange = useCallback(
    (projectsPercentageInputValue: string): void => {
      const clampedProjects = clampPercentageInput(
        projectsPercentageInputValue,
      );

      setValue(projectsPercentageFieldName, clampedProjects);
      setValue(
        filestreamPercentageFieldName,
        clampedProjects === null ? null : MAX_PERCENTAGE - clampedProjects,
      );
    },
    [setValue, filestreamPercentageFieldName, projectsPercentageFieldName],
  );

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    formMethods,
    filestreamPercentage,
    projectsPercentage,
    onPercentageSliderChange,
    onProjectsPercentageInputChange,
  };
};

export default useEditGroupForm;
